// src/TxActionDemo.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import {
  doc, getDoc, updateDoc, serverTimestamp,
  addDoc, collection
} from "firebase/firestore";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function TxActionDemo() {
  const q = useQuery();
  const navigate = useNavigate();

  const txId   = q.get("tx");
  const action = (q.get("a") || "").toLowerCase();    // "confirm-only" | "confirm-rule"
  const cat    = (q.get("cat") || "").trim();
  const sub    = (q.get("sub") || "").trim();
  const vendorFromQs = (q.get("vendor") || "").trim();

  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(true);
  const [msg, setMsg]   = useState("");
  const [err, setErr]   = useState("");
  const [tx, setTx]     = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!user || !txId) { setBusy(false); return; }
      try {
        const ref = doc(db, "transactions", txId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErr("Transaction not found.");
          setBusy(false);
          return;
        }
        const data = snap.data();
        setTx({ id: snap.id, ...data });

        if (!["confirm-only", "confirm-rule"].includes(action)) {
          setMsg("No action taken.");
          setBusy(false);
          return;
        }

        // Build the update for confirming a transaction
        const safeCategory    = cat || "Uncategorized";
        const safeSubcategory = sub || "";
        const now = serverTimestamp();

        // Write both the normalized "matched*" fields you use in the UI
        // and the persisted "category" + "categoryMatch" map you showed.
        const txUpdate = {
          category: safeCategory,
          categoryMatch: {
            category: safeCategory,
            subcategory: safeSubcategory,
            source: "user",
            type: "confirmed"
          },
          matchedCategory: safeCategory,
          matchedSubcategory: safeSubcategory,
          matchedSource: "confirmed",
          confirmed: true,
          updatedAt: now,
        };

        await updateDoc(ref, txUpdate);

        if (action === "confirm-rule") {
            const rulesCol = collection(db, "rules");

            // Normalize fields to match your examples
            const vendorLower = String(vendorFromQs || data.vendor || "").trim().toLowerCase();

            // In your examples, 'plaidCategory' stored the bank/plaid category text in lowercase.
            // Your txs sometimes have category as an array like ["Category Pending"].
            const plaidRaw = Array.isArray(data.category)
                ? (data.category[0] || "")
                : (data.category || "");
            const plaidLower = String(plaidRaw || "").trim().toLowerCase();

            // Match your sample docs: createdAt/updatedAt as ISO strings
            const nowIso = new Date().toISOString();

            await addDoc(rulesCol, {
                userId: user.uid,              // REQUIRED for your security rule
                vendor: vendorLower,           // e.g., "panda express"
                vendorMatchType: "contains",   // as per your sample
                plaidCategory: plaidLower,     // e.g., "category pending"
                category: safeCategory,        // chosen category
                subcategory: safeSubcategory,  // chosen subcategory ("" allowed)
                createdAt: nowIso,
                updatedAt: nowIso,
            });
          }

        if (!cancelled) {
          setMsg(action === "confirm-rule"
            ? "Transaction confirmed and rule saved."
            : "Transaction confirmed.");
        }
      } catch (e) {
        setErr(e?.message || String(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [user, txId, action, cat, sub, vendorFromQs]);

  const goNeeds = () =>
    navigate(`/transactions?filter=needs${txId ? `&tx=${encodeURIComponent(txId)}` : ""}`);

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <h2 style={{ marginTop: 0 }}>Trelli — Quick Action</h2>

      {!user && <p>Please sign in to take action.</p>}

      {busy && <p>Working…</p>}
      {!busy && err && <p style={{ color: "crimson" }}>Error: {err}</p>}
      {!busy && !err && (
        <>
          <p>{msg || "Done."}</p>
          {tx && (
            <div style={{ padding: 12, border: "1px solid #eee", borderRadius: 8, marginTop: 8 }}>
              <div style={{ fontWeight: 700 }}>{tx.vendor || "Unknown"}</div>
              <div style={{ color: "#666", fontSize: 13 }}>
                ${Number(tx.amount || 0).toFixed(2)} · {tx.date?.toDate?.().toLocaleDateString?.() || ""}
              </div>
            </div>
          )}
          <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goNeeds} style={btn()}>Open Transactions (Needs)</button>
            <a
              href="about:blank"
              onClick={(e) => { e.preventDefault(); window.close?.(); }}
              style={alink()}
            >
              Close tab
            </a>
          </div>
        </>
      )}
    </div>
  );
}

const btn = () => ({
  background: "#00B074",
  color: "#fff",
  border: "none",
  padding: "10px 14px",
  borderRadius: 10,
  fontWeight: 700,
  cursor: "pointer",
});

const alink = () => ({
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #ddd",
  textDecoration: "none",
  color: "#111",
  fontWeight: 600,
});